import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Animated } from "react-animated-css";
import { 
    Input, Button, Table, Modal, Form, 
    Space, Popconfirm, message, Spin, 
    Typography, Switch, Row, Col, Card,
    Tag, Badge, Radio, Checkbox, Dropdown,
    Menu, Statistic, Divider, Select
} from 'antd';
import { 
    PlusOutlined, EditOutlined, 
    DeleteOutlined, SearchOutlined,
    MoreOutlined, AppstoreOutlined,
    UnorderedListOutlined, CheckOutlined,
    CloseOutlined, DownOutlined
} from '@ant-design/icons';

import './KeywordMapping.scss';

// Import actions
import { 
    getKeywordMappings, 
    addKeywordMapping, 
    updateKeywordMapping, 
    deleteKeywordMapping 
} from '../../modules/keywordMapping.js';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

class KeywordMapping extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            mappings: [],
            filteredMappings: [],
            modalVisible: false,
            modalTitle: 'Add Keyword Mapping',
            editingMapping: null,
            searchText: '',
            viewMode: 'grouped', // 'grouped' | 'table'
            selectedKeywords: [], // for batch operations
            selectedMappings: [], // for batch operations in table view
            batchActionVisible: false,
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0
            },
            filters: {
                status: 'all', // 'all' | 'active' | 'inactive'
                sortBy: 'updated_at' // 'updated_at' | 'created_at' | 'keyword'
            },
            pendingChanges: {}, // Track pending changes by ID
            isPolling: false,   // Flag to track if polling is active
            pollingAttempts: 0, // Count polling attempts
            maxPollingAttempts: 10, // Maximum polling attempts (10 * 2s = 20s max wait time)
        };
        this.formRef = React.createRef();
        this.refreshTimer = null;
    }

    componentDidMount() {
        this.fetchKeywordMappings();
    }

    componentWillUnmount() {
        // Clear timer when component unmounts
        this.stopPolling();
    }

    // Start polling for changes
    startPolling = () => {
        if (!this.refreshTimer && !this.state.isPolling) {
            this.setState({ 
                isPolling: true,
                pollingAttempts: 0
            });
            
            this.refreshTimer = setInterval(this.pollForChanges, 2000); // Check every 2 seconds
        }
    };

    // Stop polling
    stopPolling = () => {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
            this.setState({ isPolling: false });
        }
    };

    // Poll for changes
    pollForChanges = () => {
        const { pendingChanges, pollingAttempts, maxPollingAttempts } = this.state;
        
        // If no pending changes or max attempts reached, stop polling
        if (Object.keys(pendingChanges).length === 0 || pollingAttempts >= maxPollingAttempts) {
            this.stopPolling();
            
            // If stopped due to max attempts, show notification
            if (pollingAttempts >= maxPollingAttempts && Object.keys(pendingChanges).length > 0) {
                message.info('Some changes may take longer to be reflected in the system.');
            }
            return;
        }
        
        // Increment attempt count
        this.setState({ pollingAttempts: pollingAttempts + 1 });
        
        // Check if changes have been reflected
        this.checkForUpdates();
    };

    // Check if updates have been applied
    checkForUpdates = () => {
        // Don't show loading state to avoid affecting user experience
        this.props.getKeywordMappings({env: localStorage.env})
            .then(response => {
                const freshMappings = response.results.map(item => ({
                    ...item,
                    key: item._id || item.id
                }));
                
                // Check if all pending changes have been reflected
                const { pendingChanges } = this.state;
                const updatedPendingChanges = {...pendingChanges};
                let changesApplied = false;
                
                // Check each pending change
                Object.keys(pendingChanges).forEach(id => {
                    const change = pendingChanges[id];
                    
                    switch (change.type) {
                        case 'add':
                            // Check if new item exists
                            const addedItem = freshMappings.find(item => 
                                item.keyword === change.data.keyword && 
                                item.contentId === change.data.contentId
                            );
                            if (addedItem) {
                                delete updatedPendingChanges[id];
                                changesApplied = true;
                            }
                            break;
                        
                        case 'update':
                            // Check if item has been updated
                            const updatedItem = freshMappings.find(item => 
                                (item._id || item.id) === id
                            );
                            if (updatedItem && 
                                updatedItem.active === change.data.active && 
                                updatedItem.keyword === change.data.keyword && 
                                updatedItem.contentId === change.data.contentId) {
                                delete updatedPendingChanges[id];
                                changesApplied = true;
                            }
                            break;
                        
                        case 'delete':
                            // Check if item has been deleted
                            const deletedItem = freshMappings.find(item => 
                                (item._id || item.id) === id
                            );
                            if (!deletedItem) {
                                delete updatedPendingChanges[id];
                                changesApplied = true;
                            }
                            break;
                    }
                });
                
                // If changes have been applied, update state
                if (changesApplied) {
                    this.setState({ 
                        mappings: freshMappings,
                        filteredMappings: this.applyFilters(freshMappings),
                        pendingChanges: updatedPendingChanges
                    });
                    
                    // If all changes have been applied, stop polling
                    if (Object.keys(updatedPendingChanges).length === 0) {
                        this.stopPolling();
                        message.success('All changes have been successfully applied.');
                    }
                }
            })
            .catch(error => {
                console.error('Failed to check for updates:', error);
                // Don't stop polling on error, continue trying
            });
    };

    fetchKeywordMappings = () => {
        this.setState({ loading: true });
        
        this.props.getKeywordMappings({env: localStorage.env})
            .then(response => {
                const mappings = response.results.map(item => ({
                    ...item,
                    key: item._id || item.id
                }));
                
                this.setState({ 
                    mappings,
                    filteredMappings: this.applyFilters(mappings),
                    loading: false,
                    selectedKeywords: [],
                    selectedMappings: [],
                    pendingChanges: {}, // Clear pending changes
                    pagination: {
                        ...this.state.pagination,
                        total: mappings.length
                    }
                });
            })
            .catch(error => {
                console.error('Failed to fetch keyword mappings:', error);
                message.error('Failed to load keyword mappings');
                this.setState({ loading: false });
            });
    };

    applyFilters = (mappings = this.state.mappings) => {
        const { searchText, filters } = this.state;
        let filtered = [...mappings];

        // Search filter
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(mapping => 
                mapping.keyword.toLowerCase().includes(search) || 
                mapping.contentId.toLowerCase().includes(search)
            );
        }

        // Status filter
        if (filters.status !== 'all') {
            filtered = filtered.filter(mapping => 
                filters.status === 'active' ? mapping.active : !mapping.active
            );
        }

        // Sort
        filtered.sort((a, b) => {
            switch (filters.sortBy) {
                case 'keyword':
                    return a.keyword.localeCompare(b.keyword);
                case 'created_at':
                    return new Date(b.created_at) - new Date(a.created_at);
                default:
                    return new Date(b.updated_at) - new Date(a.updated_at);
            }
        });

        return filtered;
    };

    handleSearch = (value) => {
        this.setState({
            searchText: value,
            filteredMappings: this.applyFilters()
        }, () => {
            this.setState({
                filteredMappings: this.applyFilters()
            });
        });
    };

    handleFilterChange = (key, value) => {
        this.setState({
            filters: {
                ...this.state.filters,
                [key]: value
            }
        }, () => {
            this.setState({
                filteredMappings: this.applyFilters()
            });
        });
    };

    groupMappingsByKeyword = () => {
        const { filteredMappings } = this.state;
        const grouped = {};
        
        filteredMappings.forEach(mapping => {
            if (!grouped[mapping.keyword]) {
                grouped[mapping.keyword] = [];
            }
            grouped[mapping.keyword].push(mapping);
        });
        
        return grouped;
    };

    calculateStats = () => {
        const { mappings } = this.state;
        const uniqueKeywords = new Set(mappings.map(m => m.keyword));
        
        return {
            totalKeywords: uniqueKeywords.size,
            totalMappings: mappings.length,
            activeMappings: mappings.filter(m => m.active).length,
            inactiveMappings: mappings.filter(m => !m.active).length
        };
    };

    showAddModal = (defaultKeyword = '') => {
        this.setState({
            modalVisible: true,
            modalTitle: 'Add Keyword Mapping',
            editingMapping: null
        });
        setTimeout(() => {
            this.formRef.current?.resetFields();
            if (defaultKeyword) {
                this.formRef.current?.setFieldsValue({ keyword: defaultKeyword });
            }
        }, 0);
    };

    showEditModal = (record) => {
        this.setState({
            modalVisible: true,
            modalTitle: 'Edit Keyword Mapping',
            editingMapping: record
        });
        
        setTimeout(() => {
            this.formRef.current?.setFieldsValue({
                keyword: record.keyword || '',
                contentId: record.contentId,
                active: record.active
            });
        }, 0);
    };

    handleCancel = () => {
        this.setState({ modalVisible: false });
    };

    // Render pending status indicators
    renderPendingStatus = (id) => {
        const { pendingChanges } = this.state;
        if (pendingChanges[id]) {
            const { type } = pendingChanges[id];
            let color, text;
            
            switch (type) {
                case 'add':
                    color = 'blue';
                    text = 'Adding';
                    break;
                case 'update':
                    color = 'orange';
                    text = 'Updating';
                    break;
                case 'delete':
                    color = 'red';
                    text = 'Deleting';
                    break;
                default:
                    color = 'default';
                    text = 'Pending';
            }
            
            return <Tag color={color}>{text}</Tag>;
        }
        return null;
    };

    handleSubmit = (values) => {
        const { editingMapping, mappings, pendingChanges } = this.state;
        const mappingData = {
            ...values,
            env: localStorage.env,
        };

        // Create a unique ID for the operation
        const id = editingMapping ? (editingMapping._id || editingMapping.id) : `new-${Date.now()}`;
        const operationType = editingMapping ? 'update' : 'add';
        
        // Apply optimistic update to local state
        let updatedMappings = [...mappings];
        
        if (operationType === 'update') {
            updatedMappings = mappings.map(item => 
                (item._id || item.id) === id ? { ...item, ...mappingData } : item
            );
        } else {
            // For new items, add a temporary entry
            const newMapping = {
                ...mappingData,
                _id: id,
                id: id,
                key: id,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };
            updatedMappings.push(newMapping);
        }
        
        // Track the pending change
        const newPendingChanges = {
            ...pendingChanges,
            [id]: {
                type: operationType,
                data: mappingData,
                timestamp: Date.now()
            }
        };
        
        // Update state immediately for responsive UI
        this.setState({ 
            mappings: updatedMappings,
            filteredMappings: this.applyFilters(updatedMappings),
            modalVisible: false,
            pendingChanges: newPendingChanges
        });

        // Show loading indicator for the operation
        const loadingMessage = message.loading(
            `${operationType === 'update' ? 'Updating' : 'Adding'} keyword mapping...`, 
            0
        );

        // Make the actual API call
        const apiCall = operationType === 'update' 
            ? this.props.updateKeywordMapping({
                id: id,
                ...mappingData
              })
            : this.props.addKeywordMapping(mappingData);

        apiCall
            .then(() => {
                loadingMessage();
                message.success(`Keyword mapping ${operationType === 'update' ? 'updated' : 'added'} successfully`);
                
                // Start polling to check if changes have been applied
                this.startPolling();
            })
            .catch(error => {
                loadingMessage();
                console.error(`Failed to ${operationType} keyword mapping:`, error);
                message.error(`Failed to ${operationType} keyword mapping`);
                
                // Revert the optimistic update on error
                this.setState(prevState => {
                    const revertedPendingChanges = {...prevState.pendingChanges};
                    delete revertedPendingChanges[id];
                    
                    // If it was an update, revert to original data
                    if (operationType === 'update') {
                        const originalMappings = prevState.mappings.map(item =>
                            (item._id || item.id) === id ? editingMapping : item
                        );
                        return {
                            mappings: originalMappings,
                            filteredMappings: this.applyFilters(originalMappings),
                            pendingChanges: revertedPendingChanges
                        };
                    }
                    
                    // If it was an add, remove the temporary entry
                    const originalMappings = prevState.mappings.filter(item => 
                        (item._id || item.id) !== id
                    );
                    
                    return {
                        mappings: originalMappings,
                        filteredMappings: this.applyFilters(originalMappings),
                        pendingChanges: revertedPendingChanges
                    };
                });
            });
    };

    handleDelete = (id) => {
        const { mappings, pendingChanges } = this.state;
        
        // Find the item to be deleted
        const itemToDelete = mappings.find(item => (item._id || item.id) === id);
        if (!itemToDelete) return;
        
        // Apply optimistic update - remove from local state
        const updatedMappings = mappings.filter(item => (item._id || item.id) !== id);
        
        // Track the pending change
        const newPendingChanges = {
            ...pendingChanges,
            [id]: {
                type: 'delete',
                data: itemToDelete,
                timestamp: Date.now()
            }
        };
        
        // Update state immediately
        this.setState({
            mappings: updatedMappings,
            filteredMappings: this.applyFilters(updatedMappings),
            pendingChanges: newPendingChanges
        });
        
        // Show loading indicator
        const loadingMessage = message.loading('Deleting keyword mapping...', 0);
        
        // Make the actual API call
        this.props.deleteKeywordMapping({
            id,
            env: localStorage.env
        })
            .then(() => {
                loadingMessage();
                message.success('Keyword mapping deleted successfully');
                
                // Start polling to check if changes have been applied
                this.startPolling();
            })
            .catch(error => {
                loadingMessage();
                console.error('Failed to delete keyword mapping:', error);
                message.error('Failed to delete keyword mapping');
                
                // Revert the optimistic delete on error
                this.setState(prevState => {
                    const revertedMappings = [...prevState.mappings, itemToDelete];
                    const revertedPendingChanges = {...prevState.pendingChanges};
                    delete revertedPendingChanges[id];
                    
                    return {
                        mappings: revertedMappings,
                        filteredMappings: this.applyFilters(revertedMappings),
                        pendingChanges: revertedPendingChanges
                    };
                });
            });
    };

    handleToggleActive = (record) => {
        const { mappings, pendingChanges } = this.state;
        const id = record._id || record.id;
        
        // Create updated mapping
        const updatedMapping = {
            ...record,
            active: !record.active,
            env: localStorage.env
        };
        
        // Apply optimistic update
        const updatedMappings = mappings.map(item => 
            (item._id || item.id) === id ? {...item, active: !item.active} : item
        );
        
        // Track the pending change
        const newPendingChanges = {
            ...pendingChanges,
            [id]: {
                type: 'update',
                data: updatedMapping,
                timestamp: Date.now()
            }
        };
        
        // Update state immediately
        this.setState({
            mappings: updatedMappings,
            filteredMappings: this.applyFilters(updatedMappings),
            pendingChanges: newPendingChanges
        });
        
        // Show loading indicator
        const loadingMessage = message.loading(
            `${updatedMapping.active ? 'Activating' : 'Deactivating'} keyword mapping...`, 
            0
        );
        
        // Make the actual API call
        this.props.updateKeywordMapping({
            id,
            ...updatedMapping
        })
            .then(() => {
                loadingMessage();
                message.success(`Keyword mapping ${updatedMapping.active ? 'activated' : 'deactivated'} successfully`);
                
                // Start polling to check if changes have been applied
                this.startPolling();
            })
            .catch(error => {
                loadingMessage();
                console.error('Failed to update keyword mapping status:', error);
                message.error('Failed to update keyword mapping status');
                
                // Revert the optimistic update on error
                this.setState(prevState => {
                    const revertedMappings = prevState.mappings.map(item =>
                        (item._id || item.id) === id ? {...item, active: record.active} : item
                    );
                    const revertedPendingChanges = {...prevState.pendingChanges};
                    delete revertedPendingChanges[id];
                    
                    return {
                        mappings: revertedMappings,
                        filteredMappings: this.applyFilters(revertedMappings),
                        pendingChanges: revertedPendingChanges
                    };
                });
            });
    };

    // Batch operations
    handleKeywordSelect = (keyword, checked) => {
        const { selectedKeywords } = this.state;
        if (checked) {
            this.setState({
                selectedKeywords: [...selectedKeywords, keyword],
                batchActionVisible: true
            });
        } else {
            const newSelected = selectedKeywords.filter(k => k !== keyword);
            this.setState({
                selectedKeywords: newSelected,
                batchActionVisible: newSelected.length > 0
            });
        }
    };

    handleSelectAllKeywords = (checked) => {
        const groupedMappings = this.groupMappingsByKeyword();
        const allKeywords = Object.keys(groupedMappings);
        
        this.setState({
            selectedKeywords: checked ? allKeywords : [],
            batchActionVisible: checked
        });
    };

    handleBatchActivate = () => {
        const { selectedKeywords, mappings } = this.state;
        const mappingsToUpdate = mappings.filter(m => selectedKeywords.includes(m.keyword));
        
        this.executeBatchUpdate(mappingsToUpdate, { active: true });
    };

    handleBatchDeactivate = () => {
        const { selectedKeywords, mappings } = this.state;
        const mappingsToUpdate = mappings.filter(m => selectedKeywords.includes(m.keyword));
        
        this.executeBatchUpdate(mappingsToUpdate, { active: false });
    };

    handleBatchDelete = () => {
        const { selectedKeywords, mappings } = this.state;
        const mappingsToDelete = mappings.filter(m => selectedKeywords.includes(m.keyword));
        
        Modal.confirm({
            title: 'Confirm Batch Delete',
            content: `Are you sure you want to delete ${mappingsToDelete.length} mappings from ${selectedKeywords.length} keywords?`,
            onOk: () => {
                this.executeBatchDelete(mappingsToDelete);
            }
        });
    };

    executeBatchUpdate = (mappings, updateData) => {
        this.setState({ loading: true });
        
        // Track pending changes
        const { pendingChanges } = this.state;
        const newPendingChanges = {...pendingChanges};
        
        // Apply optimistic updates
        const updatedMappings = this.state.mappings.map(mapping => {
            const shouldUpdate = mappings.some(m => 
                (m._id || m.id) === (mapping._id || mapping.id)
            );
            
            if (shouldUpdate) {
                const id = mapping._id || mapping.id;
                newPendingChanges[id] = {
                    type: 'update',
                    data: {...mapping, ...updateData},
                    timestamp: Date.now()
                };
                return {...mapping, ...updateData};
            }
            
            return mapping;
        });
        
        this.setState({
            mappings: updatedMappings,
            filteredMappings: this.applyFilters(updatedMappings),
            pendingChanges: newPendingChanges,
            loading: false,
            selectedKeywords: [],
            batchActionVisible: false
        });
        
        const updatePromises = mappings.map(mapping => 
            this.props.updateKeywordMapping({
                id: mapping._id || mapping.id,
                ...mapping,
                ...updateData,
                env: localStorage.env
            })
        );

        Promise.all(updatePromises)
            .then(() => {
                message.success(`Successfully updated ${mappings.length} mappings`);
                // Start polling to check if changes have been applied
                this.startPolling();
            })
            .catch(error => {
                console.error('Failed to execute batch update:', error);
                message.error('Failed to update some mappings');
                
                // Revert optimistic updates on error
                this.fetchKeywordMappings();
            });
    };

    executeBatchDelete = (mappings) => {
        this.setState({ loading: true });
        
        // Track pending changes
        const { pendingChanges } = this.state;
        const newPendingChanges = {...pendingChanges};
        
        // Apply optimistic deletes
        const remainingMappings = this.state.mappings.filter(mapping => {
            const shouldDelete = mappings.some(m => 
                (m._id || m.id) === (mapping._id || mapping.id)
            );
            
            if (shouldDelete) {
                const id = mapping._id || mapping.id;
                newPendingChanges[id] = {
                    type: 'delete',
                    data: mapping,
                    timestamp: Date.now()
                };
                return false;
            }
            
            return true;
        });
        
        this.setState({
            mappings: remainingMappings,
            filteredMappings: this.applyFilters(remainingMappings),
            pendingChanges: newPendingChanges,
            loading: false,
            selectedKeywords: [],
            batchActionVisible: false
        });
        
        const deletePromises = mappings.map(mapping => 
            this.props.deleteKeywordMapping({
                id: mapping._id || mapping.id,
                env: localStorage.env
            })
        );

        Promise.all(deletePromises)
            .then(() => {
                message.success(`Successfully deleted ${mappings.length} mappings`);
                // Start polling to check if changes have been applied
                this.startPolling();
            })
            .catch(error => {
                console.error('Failed to execute batch delete:', error);
                message.error('Failed to delete some mappings');
                
                // Revert optimistic deletes on error
                this.fetchKeywordMappings();
            });
    };

    renderStatsCards = () => {
        const stats = this.calculateStats();
        
        return (
            <Row gutter={16} className="stats-row">
                <Col span={6}>
                    <Card className="stat-card">
                        <Statistic 
                            title="Total Keywords" 
                            value={stats.totalKeywords}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card className="stat-card">
                        <Statistic 
                            title="Total Mappings" 
                            value={stats.totalMappings}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card className="stat-card">
                        <Statistic 
                            title="Active Mappings" 
                            value={stats.activeMappings}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card className="stat-card">
                        <Statistic 
                            title="Inactive Mappings" 
                            value={stats.inactiveMappings}
                            valueStyle={{ color: '#f5222d' }}
                        />
                    </Card>
                </Col>
            </Row>
        );
    };

    renderBatchActions = () => {
        const { selectedKeywords, batchActionVisible } = this.state;
        
        if (!batchActionVisible) return null;

        const batchMenu = (
            <Menu>
                <Menu.Item key="activate" onClick={this.handleBatchActivate}>
                    <CheckOutlined /> Activate Selected
                </Menu.Item>
                <Menu.Item key="deactivate" onClick={this.handleBatchDeactivate}>
                    <CloseOutlined /> Deactivate Selected
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item key="delete" danger onClick={this.handleBatchDelete}>
                    <DeleteOutlined /> Delete Selected
                </Menu.Item>
            </Menu>
        );

        return (
            <div className="batch-actions">
                <Space>
                    <span>{selectedKeywords.length} keywords selected</span>
                    <Dropdown overlay={batchMenu} trigger={['click']}>
                        <Button>
                            Batch Actions <DownOutlined />
                        </Button>
                    </Dropdown>
                    <Button onClick={() => this.setState({ selectedKeywords: [], batchActionVisible: false })}>
                        Clear Selection
                    </Button>
                </Space>
            </div>
        );
    };

    renderGroupedView = () => {
        const groupedMappings = this.groupMappingsByKeyword();
        const { selectedKeywords } = this.state;
        const { userProfile } = this.props;
        let canEdit = (userProfile && userProfile.user && userProfile.user.access.includes('search-recommendation:editor')) ? true : false;
        
        if (Object.keys(groupedMappings).length === 0) {
            return (
                <div className="empty-state">
                    <p>No keyword mappings found</p>
                </div>
            );
        }

        return (
            <div className="grouped-mappings">
                {
                    canEdit &&
                    <div className="group-header">
                        <Checkbox 
                            onChange={(e) => this.handleSelectAllKeywords(e.target.checked)}
                            checked={selectedKeywords.length === Object.keys(groupedMappings).length}
                            indeterminate={selectedKeywords.length > 0 && selectedKeywords.length < Object.keys(groupedMappings).length}
                        >
                            Select All Keywords
                        </Checkbox>
                    </div>
                }
                
                {Object.entries(groupedMappings).map(([keyword, mappings]) => (
                    <Card 
                        key={keyword}
                        className={`keyword-group-card ${selectedKeywords.includes(keyword) ? 'selected' : ''}`}
                        title={
                            <div className="keyword-header">
                                {
                                    canEdit &&
                                    <Checkbox 
                                        checked={selectedKeywords.includes(keyword)}
                                        onChange={(e) => this.handleKeywordSelect(keyword, e.target.checked)}
                                    />
                                }
                             
                                <span className="keyword-title">{keyword}</span>
                                <Badge count={mappings.length} style={{ backgroundColor: '#52c41a' }} />
                                <div className="keyword-stats">
                                    <Tag color="green">{mappings.filter(m => m.active).length} Active</Tag>
                                    <Tag color="red">{mappings.filter(m => !m.active).length} Inactive</Tag>
                                </div>
                            </div>
                        }
                        extra={
                            canEdit ?
                                <Button 
                                    type="primary" 
                                    size="small"
                                    onClick={() => this.showAddModal(keyword)}
                                >
                                    Add Content
                                </Button>
                            : ""
                        }
                    >
                        <div className="content-mappings">
                            {mappings.map(mapping => (
                                <div key={mapping.key} className="content-item">
                                    <div className="content-info">
                                        <span className="content-id">{mapping.contentId}</span>
                                        <div className="content-meta">
                                            <Tag color={mapping.active ? 'green' : 'red'}>
                                                {mapping.active ? 'Active' : 'Inactive'}
                                            </Tag>
                                            <span className="date-info">
                                                Updated: {new Date(mapping.updated_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                    { 
                                        canEdit &&
                                        <div className="content-actions">
                                            <Switch 
                                                size="small"
                                                checked={mapping.active} 
                                                onChange={() => this.handleToggleActive(mapping)}
                                            />
                                            <Button 
                                                type="text" 
                                                size="small"
                                                icon={<EditOutlined />} 
                                                onClick={() => this.showEditModal(mapping)}
                                            />
                                            <Popconfirm
                                                title="Delete this mapping?"
                                                onConfirm={() => this.handleDelete(mapping._id || mapping.id)}
                                            >
                                                <Button 
                                                    type="text" 
                                                    size="small"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                />
                                            </Popconfirm>
                                        </div>
                                    }
                                </div>
                            ))}
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    renderTableView = () => {
        const { filteredMappings, loading, pagination } = this.state;
        const { userProfile } = this.props;
        const canEdit = (userProfile && userProfile.user && userProfile.user.access.includes('search-recommendation:editor')) ? true : false;

        const columns = [
            {
                title: 'Keyword',
                dataIndex: 'keyword',
                key: 'keyword',
                sorter: (a, b) => a.keyword.localeCompare(b.keyword),
            },
            {
                title: 'Content ID',
                dataIndex: 'contentId',
                key: 'contentId',
                sorter: (a, b) => a.contentId.localeCompare(b.contentId),
            },
            {
                title: 'Created At',
                dataIndex: 'created_at',
                key: 'created_at',
                render: (text) => new Date(text).toLocaleString(),
                sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
            },
            {
                title: 'Updated At',
                dataIndex: 'updated_at',
                key: 'updated_at',
                render: (text) => new Date(text).toLocaleString(),
                sorter: (a, b) => new Date(a.updated_at) - new Date(b.updated_at),
            },
            {
                title: 'Active',
                dataIndex: 'active',
                key: 'active',
                render: (active, record) => (
                    <Switch 
                        disabled={!canEdit}
                        checked={active} 
                        onChange={() => this.handleToggleActive(record)}
                    />
                ),
                filters: [
                    { text: 'Active', value: true },
                    { text: 'Inactive', value: false },
                ],
                onFilter: (value, record) => record.active === value,
            },
        ];

        if (canEdit) {
            columns.push({
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                    <Space size="middle">
                        <Button 
                            type="primary" 
                            icon={<EditOutlined />} 
                            onClick={() => this.showEditModal(record)}
                        />
                        <Popconfirm
                            title="Delete Keyword Mapping"
                            description="Are you sure you want to delete this mapping?"
                            onConfirm={() => this.handleDelete(record._id || record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button 
                                danger 
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Space>
                ),
            })
        }

        return (
            <Table
                columns={columns}
                dataSource={filteredMappings}
                loading={loading}
                pagination={pagination}
                onChange={this.handleTableChange}
                rowKey={record => record._id || record.id}
            />
        );
    };

    render() {
        const { 
            loading, modalVisible, modalTitle, editingMapping, viewMode, filters 
        } = this.state;
        const { userProfile } = this.props;
		let canEdit = (userProfile && userProfile.user && userProfile.user.access.includes('search-recommendation:editor')) ? true : false;

        return (
            <Animated animationIn="fadeIn">
                <div className="keyword-mapping-container">
                    {/* Header */}
                    <Row className="header-row" justify="space-between" align="bottom">
                        <Col>
                            <Title level={2}>Keyword Mapping Management</Title>
                        </Col>
                        <Col>
                            <Space>
                                <Radio.Group 
                                    block
                                    size='large'
                                    value={viewMode} 
                                    onChange={(e) => this.setState({ viewMode: e.target.value })}
                                    className="view-toggle"
                                >
                                    <Radio.Button value="grouped" optionType="button">
                                        <AppstoreOutlined /> Grouped
                                    </Radio.Button>
                                    <Radio.Button value="table" optionType="button">
                                        <UnorderedListOutlined /> Table
                                    </Radio.Button>
                                </Radio.Group>
                                {
                                    canEdit &&
                                    <Button 
                                        type="primary" 
                                        icon={<PlusOutlined />} 
                                        onClick={() => this.showAddModal()}
                                        size="large"
                                    >
                                        Add Mapping
                                    </Button>
                                }
                            </Space>
                        </Col>
                    </Row>

                    {/* Stats Cards */}
                    {this.renderStatsCards()}

                    {/* Filters and Search */}
                    <Row className="filter-row" gutter={16}>
                        <Col span={8}>
                            <Search
                                placeholder="Search by keyword or content ID"
                                allowClear
                                enterButton={<SearchOutlined />}
                                size="large"
                                onSearch={this.handleSearch}
                            />
                        </Col>
                        <Col span={4}>
                            <Select
                                value={filters.status}
                                onChange={(value) => this.handleFilterChange('status', value)}
                                style={{ width: '100%' }}
                                size="large"
                            >
                                <Option value="all">All Status</Option>
                                <Option value="active">Active Only</Option>
                                <Option value="inactive">Inactive Only</Option>
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                value={filters.sortBy}
                                onChange={(value) => this.handleFilterChange('sortBy', value)}
                                style={{ width: '100%' }}
                                size="large"
                            >
                                <Option value="updated_at">Sort by Updated</Option>
                                <Option value="created_at">Sort by Created</Option>
                                <Option value="keyword">Sort by Keyword</Option>
                            </Select>
                        </Col>
                        <Col span={8}>
                            <Space>
                                <Button 
                                    onClick={this.fetchKeywordMappings}
                                    loading={loading}
                                    size='large'
                                >
                                    Refresh
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    {/* Batch Actions */}
                    {this.renderBatchActions()}

                    {/* Content */}
                    <div className="content-area">
                        {viewMode === 'grouped' ? this.renderGroupedView() : this.renderTableView()}
                    </div>

                    {/* Modal */}
                    <Modal
                        title={modalTitle}
                        open={modalVisible}
                        onCancel={this.handleCancel}
                        footer={null}
                        width={600}
                    >
                        <Form
                            ref={this.formRef}
                            layout="vertical"
                            onFinish={this.handleSubmit}
                        >
                            <Form.Item
                                name="keyword"
                                label="Keyword"
                                rules={[
                                    { required: true, message: 'Please enter the keyword' },
                                    { min: 2, message: 'Keyword must be at least 2 characters' }
                                ]}
                            >
                                <Input placeholder="Enter keyword" />
                            </Form.Item>
                            
                            <Form.Item
                                name="contentId"
                                label="Content ID"
                                rules={[{ required: true, message: 'Please enter the content ID' }]}
                            >
                                <Input placeholder="Enter content ID" />
                            </Form.Item>
                            
                            <Form.Item
                                name="active"
                                label="Active"
                                valuePropName="checked"
                                initialValue={true}
                            >
                                <Switch />
                            </Form.Item>
                            
                            <Form.Item>
                                <Space>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        {editingMapping ? 'Update' : 'Add'}
                                    </Button>
                                    <Button onClick={this.handleCancel}>
                                        Cancel
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>
            </Animated>
        );
    }
}

const mapStateToProps = state => ({
    router: state.router,
    userProfile: state.user.userProfile,
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getKeywordMappings,
            addKeywordMapping,
            updateKeywordMapping,
            deleteKeywordMapping,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(KeywordMapping);
